import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import SearchIcon from "@mui/icons-material/Search";
import { Button as MButton } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { ReactComponent as Alert } from "assets/alert-information-circle.svg";
import { ReactComponent as EmptyFolder } from "assets/empty-folder.svg";
import { setInnCode, setInncodes } from "components/app/App.slice";
import ThemeContext from "components/app/Context";
import AutocompleteInncode from "components/common/AutocompleteInncode";
import ToolTip from "components/common/Tooltip";
import Typography from "components/common/Typography";
import {
  fetchPropertyDetails,
  setProperties,
  setUserDashboardFilters
} from "components/features/InncodeUsers/userdashboard.slice";
import { setInncodeDetails } from "components/features/MyPlans/myplans.slice";
import { useTranslate } from "hooks/useTranslate";
import { uniqBy } from "lodash";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import colors from "styles/Colors";
import { tokens } from "styles/Tokens";
import { DEFAULT_CORP_INNCODE, includeAboveProperty, messages } from "utils/CommonConstants";
import { getUserTypeValue } from "utils/CommonFunctions";
import InncodeItem from "./inncodeItem";
import MemberList from "./memberList";

const InncodeUsers = () => {
  const translate = useTranslate();
  const dispatch = useAppDispatch();
  const [showTable, setShowTable] = useState(false);
  const { isMobileScreen } = useContext(ThemeContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedInncode = searchParams.get("inncode");
  const { innCode: defaultInncode } = useAppSelector((state) => state.app);
  const { properties, propertyDetails, filters } = useAppSelector((state) => state.userDashboard);
  const { bookMarkInncodes, innCodeDetails } = useAppSelector((state) => state.myplans.MyPlans);
  const { records, count }: any = properties;
  const { name, users } = propertyDetails;

  const getDefaultProperties = () => {
    let data: any = [];

    if (bookMarkInncodes?.length) {
      data = [...bookMarkInncodes];
    }

    if (innCodeDetails?.length) {
      data = [...data, ...innCodeDetails];
    }

    const finalProperties = uniqBy(data, (p: any) => p?.innCode);

    if (defaultInncode && defaultInncode !== DEFAULT_CORP_INNCODE) {
      setSearchParams({ inncode: selectedInncode ?? defaultInncode });
    } else if (data.length) {
      setSearchParams({ inncode: finalProperties[0]?.innCode });
    } else {
      searchParams.delete("inncode");
      setSearchParams(searchParams);
    }

    dispatch(setProperties({ records: finalProperties, count: finalProperties.length }));
  };

  useEffect(() => {
    getDefaultProperties();

    return () => {
      dispatch(setInncodes([]));
    };
  }, []);

  useEffect(() => {
    if (selectedInncode) {
      const roles = filters.roles.join(",");
      dispatch(
        fetchPropertyDetails({
          innCodes: selectedInncode,
          roles,
          userType: getUserTypeValue(filters[includeAboveProperty]),
          includecorpusers: false
        })
      );
    }
  }, [searchParams]);

  const handleSelectInncode = (data: any) => {
    dispatch(setInncodeDetails([data]));
    dispatch(setInnCode(data?.innCode));

    if (isMobileScreen) {
      setShowTable(true);
    }

    if (selectedInncode !== data?.innCode) {
      setSearchParams({ inncode: data?.innCode });
      dispatch(setUserDashboardFilters({ roles: [], includeAboveProperty: false }));
    }
  };

  const handleInncodeInputChange = (val: any) => {
    if (
      selectedInncode?.toLowerCase() === val?.innCode?.toLowerCase() ||
      records.some((r: any) => r?.innCode === val?.innCode) ||
      !val
    ) {
      return;
    }

    dispatch(setInnCode(val?.innCode));
    setSearchParams({ inncode: val?.innCode });
    dispatch(setProperties({ records: [...records, val], count: [...records, val].length }));
  };

  return (
    <>
      {!showTable && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            paddingY: isMobileScreen ? "0" : "24px",
            borderBottom: isMobileScreen ? "none" : `1px solid ${tokens.Border.General.Tertiary}`
          }}
        >
          <Typography variant="h4" showBold color={tokens.Text.General.PrimaryHeadline}>
            Impersonate
          </Typography>
          <ToolTip title={translate("impersonate.header.tooltip")}>
            <Alert />
          </ToolTip>
        </Box>
      )}
      <Stack direction={isMobileScreen ? "column" : "row"}>
        {!showTable && (
          <Stack
            sx={{
              width: isMobileScreen ? "100%" : "400px",
              borderRight: isMobileScreen ? "none" : `1px solid ${tokens.Border.General.Tertiary}`
            }}
          >
            <Stack
              spacing={1}
              direction="row"
              alignItems="center"
              sx={{
                pt: "24px",
                pb: isMobileScreen ? "0" : "24px",
                pr: isMobileScreen ? 0 : "24px",
                borderBottom: isMobileScreen ? "none" : `1px solid ${tokens.Border.General.Tertiary}`
              }}
            >
              <Typography variant="h5" showBold color={tokens.Text.General.PrimaryHeadline} sx={{ flex: 1 }}>
                Properties
              </Typography>
              <Typography variant="body2" color={tokens.Text.General.Tertiary}>
                {count} properties
              </Typography>
            </Stack>
            <Box sx={{ padding: isMobileScreen ? "8px 0 24px 0" : "24px 24px 24px 0" }}>
              <AutocompleteInncode
                startIcon={<SearchIcon />}
                onSelect={handleInncodeInputChange}
                placeholder="Search by property or Inncode"
              />
            </Box>
            <Box sx={{ height: isMobileScreen ? "auto" : "calc(100vh - 338px)", overflowY: "auto" }}>
              {records.map((list: any) => (
                <InncodeItem
                  key={list?.innCode}
                  data={list}
                  onSelect={handleSelectInncode}
                  selected={selectedInncode}
                />
              ))}
            </Box>
          </Stack>
        )}

        {records.length ? (
          <Stack
            spacing={2}
            sx={{
              flex: 1,
              padding: isMobileScreen ? "8px" : "24px",
              pr: 0,
              height: isMobileScreen ? "auto" : "calc(100vh - 160px)"
            }}
          >
            {showTable && (
              <MButton
                startIcon={<NavigateBeforeIcon />}
                sx={{
                  margin: 0,
                  alignSelf: "flex-start",
                  color: tokens.Text.General.PrimaryBody,
                  fontWeight: "normal"
                }}
                onClick={() => setShowTable(false)}
              >
                Back
              </MButton>
            )}
            {isMobileScreen && showTable && (
              <>
                <Typography variant="h4" showBold color={tokens.Text.General.PrimaryHeadline}>
                  {name}
                </Typography>

                <Typography variant="body1" color={tokens.Text.General.Tertiary}>
                  {users?.count} Members
                </Typography>
                <MemberList />
              </>
            )}

            {!isMobileScreen && !showTable && (
              <>
                <Typography variant="h4" showBold color={tokens.Text.General.PrimaryHeadline}>
                  {name}
                </Typography>

                <Typography variant="body1" color={tokens.Text.General.Tertiary}>
                  {users?.count} Members
                </Typography>
                <MemberList />
              </>
            )}
          </Stack>
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              width: isMobileScreen ? "auto" : "100%",
              height: isMobileScreen ? "calc(100vh - 222px)" : "calc(100vh - 160px)",
              backgroundColor: colors.Neutral.Neutral400,
              padding: "16px"
            }}
          >
            <EmptyFolder />
            <Typography
              variant="h4"
              showBold
              color={tokens.Text.General.Inverse}
              sx={{ my: "24px", textAlign: "center" }}
            >
              {messages.noProperties}
            </Typography>
          </Stack>
        )}
      </Stack>
    </>
  );
};

export default InncodeUsers;
